import React, { useState, useEffect } from 'react';
import { Eye, CheckCircle, XCircle, RefreshCw, Landmark, Coins, FileSpreadsheet, Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from '../../ui/Loading';
import ExportConfirmModal from '../../ui/ExportConfirmModal';
import { WithdrawalRequest, WithdrawalFilters, Pagination } from '../../../types';
import { format } from 'date-fns';
import { exportToExcel } from '../../../utils';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { useTimezone } from '@kioskgaming/ui';
import { cn } from '../../../utils';
import { getWithdrawalListLabel, getWithdrawalRequesterPath } from '../../../utils/withdrawalRequester';
import { getWithdrawalTypeClassName, getWithdrawalTypeLabel, WITHDRAWAL_TYPE_FILTER_OPTIONS } from '../../../utils/withdrawalTypeDisplay';
import { isWithdrawalAdminApprovable, WITHDRAWAL_STATUS_FILTER_OPTIONS, isWithdrawalAdminRecoverable, isWithdrawalPhase3RecoveryPending } from '../../../utils/withdrawalStatusDisplay';
import WithdrawalStatusBadge from './WithdrawalStatusBadge';
import WithdrawalReviewIndicator from './WithdrawalReviewIndicator';
import { getWithdrawalReviewSummary } from '../../../utils/withdrawalFlagDisplay';
import { apiClient } from '../../../services/api';
import { toast } from 'react-hot-toast';
import { useProviderFeeConfigs } from '../../../hooks/useApi';
import {
  enrichWithdrawalsWithPortalUsdFees,
  formatWithdrawalDisplayAmount,
  getWithdrawalDisplayAmount,
} from '../../../utils/portalUsdWithdrawalDisplay';
import {
  Input as SharedInput,
  Button as SharedButton,
  DataTableShell,
  Select as SharedSelect,
  TableFilterBar,
  TableFilterField,
  TableFilterGrid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  NumberedPagination,
  SortableTableHead,
} from '@kioskgaming/ui';
import useTableSort from '../../../hooks/useTableSort';
import { getTableSortColumns } from '../../../config/tableSortConfig';
import {
  AdminTableColgroup,
  ADMIN_TABLE_FIXED_CLASS,
  adminCellClass,
  WITHDRAWAL_QUEUE_COLUMNS,
  WQ,
} from '../../../config/adminTableLayout';
import type { SortDirection } from '@kioskgaming/ui/dist/data-table/sortState';

interface WithdrawalListProps {
  withdrawals: WithdrawalRequest[];
  pagination: Pagination;
  isLoading: boolean;
  isFetching?: boolean;
  appliedSort?: { sortBy: string | null; sortOrder: SortDirection | null };
  filters: WithdrawalFilters;
  onFiltersChange: React.Dispatch<React.SetStateAction<WithdrawalFilters>>;
  onViewWithdrawal: (withdrawal: WithdrawalRequest) => void;
  onApproveWithdrawal: (withdrawal: WithdrawalRequest) => void;
  onRejectWithdrawal: (withdrawal: WithdrawalRequest) => void;
  onRecoverWithdrawal?: (withdrawal: WithdrawalRequest) => void;
  onRefresh?: () => void;
  canApproveWithdrawal?: boolean;
}

const EXPORT_LIMIT = 1000;

function FilterSearchInput({
  placeholder,
  value,
  onChange,
  ariaLabel,
  type = 'text',
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  type?: string;
}) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <SharedInput
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-10 pr-9"
        aria-label={ariaLabel}
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
          aria-label="Clear"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

const WithdrawalList: React.FC<WithdrawalListProps> = ({
  withdrawals,
  pagination,
  isLoading,
  isFetching = false,
  appliedSort,
  filters,
  onFiltersChange,
  onViewWithdrawal,
  onApproveWithdrawal,
  onRejectWithdrawal,
  onRecoverWithdrawal,
  onRefresh,
  canApproveWithdrawal = false,
}) => {
  const fmt = useFormatDate();
  const { timezone } = useTimezone();
  const { data: providerFeesData } = useProviderFeeConfigs();
  const providerFeeItems = providerFeesData?.data?.items ?? [];
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [phoneValue, setPhoneValue] = useState(filters.phone || '');
  const isShopPlayer = (w: WithdrawalRequest) => !!(w.user?.shop_id ?? w.user?.shopId);

  const tableSort = useTableSort({
    columns: getTableSortColumns('withdrawalRequests'),
    onChange: () => {
      onFiltersChange((prev) => ({ ...prev, page: 1 }));
    },
  });

  useEffect(() => {
    onFiltersChange((prev) => {
      const nextSortBy = tableSort.sortParams.sortBy;
      const nextSortOrder = tableSort.sortParams.sortOrder;
      if (prev.sortBy === nextSortBy && prev.sortOrder === nextSortOrder) {
        return prev;
      }
      const next: WithdrawalFilters = { ...prev, page: 1 };
      if (nextSortBy && nextSortOrder) {
        next.sortBy = nextSortBy as WithdrawalFilters['sortBy'];
        next.sortOrder = nextSortOrder;
      } else {
        delete next.sortBy;
        delete next.sortOrder;
      }
      return next;
    });
  }, [tableSort.sortBy, tableSort.sortOrder, onFiltersChange]);

  const headerSort = tableSort.sortBy
    ? {
        activeKey: appliedSort?.sortBy ?? tableSort.sortBy,
        direction: appliedSort?.sortOrder ?? tableSort.sortOrder,
      }
    : { activeKey: null, direction: null };

  const isTableBusy = isFetching && withdrawals.length > 0;

  useEffect(() => {
    const timer = setTimeout(() => {
      onFiltersChange((prev) => ({ ...prev, search: searchValue.trim() || undefined, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue, onFiltersChange]);

  useEffect(() => {
    const timer = setTimeout(() => {
      onFiltersChange((prev) => ({ ...prev, phone: phoneValue.trim() || undefined, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [phoneValue, onFiltersChange]);

  useEffect(() => {
    const newValue = filters.search || '';
    if (newValue !== searchValue) setSearchValue(newValue);
  }, [filters.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const newValue = filters.phone || '';
    if (newValue !== phoneValue) setPhoneValue(newValue);
  }, [filters.phone]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExportConfirm = async () => {
    setIsExporting(true);
    try {
      const exportFilters: WithdrawalFilters = {
        ...filters,
        page: 1,
        limit: EXPORT_LIMIT,
        sortBy: 'createdAt',
        sortOrder: 'DESC',
      };
      const res = await apiClient.getWithdrawals(exportFilters);
      const list = enrichWithdrawalsWithPortalUsdFees(res?.data?.withdrawals ?? [], providerFeeItems);
      const rows = list.map((w: WithdrawalRequest) => ({
        'Transaction ID': w.transactionId,
        'User ID': w.userId,
        'User (Phone)': w.user?.phone ?? w.user?.email ?? '',
        Type: getWithdrawalTypeLabel(w.withdrawalType, w),
        Amount: getWithdrawalDisplayAmount(w),
        Currency: w.currency,
        Status: w.status,
        Review: getWithdrawalReviewSummary(w)?.title ?? '',
        'Review reason': getWithdrawalReviewSummary(w)?.shortReason ?? '',
        'Created At': w.createdAt ? fmt(w.createdAt) : '',
        'Updated At': w.updatedAt ? fmt(w.updatedAt) : '',
      }));
      exportToExcel(rows, {
        sheetName: 'Withdrawals',
        fileName: `withdrawals-export-${format(new Date(), 'yyyy-MM-dd')}`,
      });
      toast.success('Export completed successfully');
      setShowExportModal(false);
    } catch (err) {
      console.error('Export withdrawals error:', err);
      toast.error('Failed to export withdrawal requests. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const getTypeBadge = (withdrawal: WithdrawalRequest) => {
    const type = withdrawal.withdrawalType;
    const label = getWithdrawalTypeLabel(type, withdrawal);
    const className = getWithdrawalTypeClassName(type, withdrawal);
    const Icon =
      type === 'bank_transfer' ? Landmark : Coins;

    return (
      <span className={cn('px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1', className)}>
        <Icon className="h-3 w-3" />
        {label}
      </span>
    );
  };

  const handlePageChange = (page: number) => {
    onFiltersChange({ ...filters, page });
  };

  const handleStatusFilter = (status: WithdrawalRequest['status'] | 'all') => {
    onFiltersChange({ ...filters, status: status === 'all' ? undefined : status, page: 1 });
  };

  const handleTypeFilter = (type: WithdrawalFilters['withdrawalType'] | 'all') => {
    onFiltersChange({ ...filters, withdrawalType: type === 'all' ? undefined : type, page: 1 });
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, dateFrom: e.target.value, page: 1 });
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, dateTo: e.target.value, page: 1 });
  };

  const handleClearFilters = () => {
    tableSort.resetSort();
    setSearchValue('');
    setPhoneValue('');
    onFiltersChange({
      page: 1,
      limit: 20,
      dateFrom: '',
      dateTo: '',
      search: undefined,
      phone: undefined,
      status: undefined,
      withdrawalType: undefined,
    });
  };

  if (isLoading && withdrawals.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Withdrawals Table */}
      <DataTableShell>
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-6">
          <div className="text-base font-semibold text-foreground">Withdrawal Requests</div>
          <div className="flex flex-wrap items-center gap-2">
            <SharedButton
              variant="outline"
              size="sm"
              onClick={() => setShowExportModal(true)}
              disabled={withdrawals.length === 0}
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Export to Excel
            </SharedButton>
            {onRefresh ? (
              <SharedButton variant="outline" size="sm" onClick={onRefresh} disabled={isLoading}>
                <RefreshCw className={cn('h-4 w-4 mr-2', isLoading && 'animate-spin')} />
                Refresh
              </SharedButton>
            ) : null}
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <TableFilterBar
            title="Filters"
            actions={
              <SharedButton variant="outline" size="sm" onClick={handleClearFilters}>
                Clear Filters
              </SharedButton>
            }
          >
            <TableFilterGrid columns={2}>
              <TableFilterField label="Transaction ID">
                <FilterSearchInput
                  placeholder="Search by transaction ID…"
                  value={searchValue}
                  onChange={setSearchValue}
                  ariaLabel="Search by transaction ID"
                />
              </TableFilterField>

              <TableFilterField label="Phone">
                <FilterSearchInput
                  placeholder="Search by phone number…"
                  value={phoneValue}
                  onChange={setPhoneValue}
                  ariaLabel="Search by phone"
                  type="tel"
                />
              </TableFilterField>
            </TableFilterGrid>

            <TableFilterGrid columns={4}>
              <TableFilterField label="Status">
                <SharedSelect
                  selectSize="md"
                  value={filters.status || 'all'}
                  onChange={(e) => handleStatusFilter(e.target.value as any)}
                  options={WITHDRAWAL_STATUS_FILTER_OPTIONS}
                  hidePlaceholder
                />
              </TableFilterField>

              <TableFilterField label="Type">
                <SharedSelect
                  selectSize="md"
                  value={filters.withdrawalType || 'all'}
                  onChange={(e) => handleTypeFilter(e.target.value as any)}
                  options={WITHDRAWAL_TYPE_FILTER_OPTIONS}
                  hidePlaceholder
                />
              </TableFilterField>

              <TableFilterField label="From Date">
                <SharedInput type="date" value={filters.dateFrom || ''} onChange={handleDateFromChange} />
              </TableFilterField>

              <TableFilterField label="To Date">
                <SharedInput type="date" value={filters.dateTo || ''} onChange={handleDateToChange} />
              </TableFilterField>
            </TableFilterGrid>
          </TableFilterBar>

          {withdrawals.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No withdrawal requests found.</p>
          ) : (
            <Table containerClassName="mt-4" className={ADMIN_TABLE_FIXED_CLASS} aria-busy={isTableBusy || undefined}>
              <AdminTableColgroup columns={WITHDRAWAL_QUEUE_COLUMNS} />
              <TableHeader>
                <TableRow>
                  <TableHead>Transaction ID</TableHead>
                  <TableHead>Requester</TableHead>
                  <TableHead>Type</TableHead>
                  <SortableTableHead
                    sortable
                    label="Amount"
                    sortKey="amount"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    align="right"
                    disabled={isTableBusy}
                  />
                  <SortableTableHead
                    sortable
                    label="Status"
                    sortKey="status"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    disabled={isTableBusy}
                  />
                  <TableHead>Review</TableHead>
                  <SortableTableHead
                    sortable
                    label="Created"
                    sortKey="createdAt"
                    activeKey={headerSort.activeKey}
                    direction={headerSort.direction}
                    onSort={tableSort.toggleSort}
                    disabled={isTableBusy}
                  />
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className={cn(isTableBusy && 'opacity-60 transition-opacity')}>
                {withdrawals.map((withdrawal) => (
                  <TableRow key={withdrawal.transactionId}>
                    <TableCell className={cn('text-sm font-mono min-w-0', adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.tx]))}>
                      <Link
                        to={`/transactions?search=${encodeURIComponent(withdrawal.transactionId)}`}
                        className="text-foreground hover:underline break-all"
                      >
                        {withdrawal.transactionId}
                      </Link>
                    </TableCell>
                    <TableCell className={cn('text-sm min-w-0', adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.party]))}>
                      {(() => {
                        const userLabel = getWithdrawalListLabel(withdrawal);
                        const requesterPath = getWithdrawalRequesterPath(withdrawal);

                        return requesterPath ? (
                          <Link
                            to={requesterPath}
                            className="font-medium text-muted-foreground hover:text-foreground hover:underline"
                          >
                            {userLabel}
                          </Link>
                        ) : (
                          <span className="font-medium text-muted-foreground">{userLabel}</span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className={adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.type])}>{getTypeBadge(withdrawal)}</TableCell>
                    <TableCell className={cn('text-sm font-medium', adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.amount]))}>
                      {formatWithdrawalDisplayAmount(withdrawal)}
                    </TableCell>
                    <TableCell className={adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.status])}>
                      <WithdrawalStatusBadge status={withdrawal.status} className="px-2 py-1" />
                    </TableCell>
                    <TableCell className={adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.review])}>
                      <WithdrawalReviewIndicator withdrawal={withdrawal} variant="compact" />
                    </TableCell>
                    <TableCell className={cn('text-sm', adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.created]))}>
                      {fmt(withdrawal.createdAt)}
                    </TableCell>
                    <TableCell className={adminCellClass(WITHDRAWAL_QUEUE_COLUMNS[WQ.actions])}>
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        <SharedButton
                          variant="outline"
                          size="sm"
                          onClick={() => onViewWithdrawal(withdrawal)}
                          className="gap-1.5 h-8 px-2.5"
                        >
                          <Eye className="h-3.5 w-3.5 shrink-0" />
                          <span className="text-xs">View</span>
                        </SharedButton>
                        {canApproveWithdrawal && isWithdrawalAdminRecoverable(withdrawal) && onRecoverWithdrawal ? (
                          <SharedButton
                            variant="outline"
                            size="sm"
                            onClick={() => onRecoverWithdrawal(withdrawal)}
                            className="gap-1.5 h-8 px-2.5 text-amber-700 hover:text-amber-800"
                          >
                            <CheckCircle className="h-3.5 w-3.5 shrink-0" />
                            <span className="text-xs">Recover</span>
                          </SharedButton>
                        ) : null}
                        {canApproveWithdrawal
                          && isWithdrawalAdminApprovable(withdrawal.status)
                          && !isWithdrawalPhase3RecoveryPending(withdrawal) ? (
                          <>
                            <SharedButton
                              variant="outline"
                              size="sm"
                              disabled={isShopPlayer(withdrawal)}
                              title={isShopPlayer(withdrawal) ? 'Shop approval only' : undefined}
                              onClick={() => onApproveWithdrawal(withdrawal)}
                              className="gap-1.5 h-8 px-2.5 text-green-600 hover:text-green-700 disabled:text-muted-foreground"
                            >
                              <CheckCircle className="h-3.5 w-3.5 shrink-0" />
                              <span className="text-xs">Approve</span>
                            </SharedButton>
                            <SharedButton
                              variant="outline"
                              size="sm"
                              disabled={isShopPlayer(withdrawal)}
                              title={isShopPlayer(withdrawal) ? 'Shop approval only' : undefined}
                              onClick={() => onRejectWithdrawal(withdrawal)}
                              className="gap-1.5 h-8 px-2.5 text-red-600 hover:text-red-700 disabled:text-muted-foreground"
                            >
                              <XCircle className="h-3.5 w-3.5 shrink-0" />
                              <span className="text-xs">Reject</span>
                            </SharedButton>
                          </>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} withdrawals
              </p>
              <NumberedPagination
                currentPage={pagination.page}
                totalPages={pagination.pages}
                onPageChange={handlePageChange}
                variant="admin"
              />
            </div>
          )}
        </div>
      </DataTableShell>

      <ExportConfirmModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={handleExportConfirm}
        isExporting={isExporting}
        title="Export to Excel"
        message="Only the latest 1000 records will be exported. Do you want to continue?"
      />
    </div>
  );
};

export default WithdrawalList;
